import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';

const BankVerificationScreen = () => {
    const [bankData, setBankData] = useState({
        accountNumber: '',
        confirmAccountNumber: '',
        ifscCode: '',
        bankName: '',
        branchName: ''
    });
    const [loading, setLoading] = useState(false);

    const navigate = useNavigate();
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

    const handleVerifyBank = async () => {
        if (!bankData.accountNumber || !bankData.confirmAccountNumber || !bankData.ifscCode) {
            toast.error('Please fill all required fields');
            return;
        }

        if (bankData.accountNumber !== bankData.confirmAccountNumber) {
            toast.error('Account numbers do not match');
            return;
        }

        if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(bankData.ifscCode)) {
            toast.error('Invalid IFSC code format');
            return;
        }

        setLoading(true);
        try {
            const userInfo = JSON.parse(localStorage.getItem('tempUserInfo') || '{}');

            const verificationData = {
                userId: userInfo.id,
                accountNumber: bankData.accountNumber,
                ifscCode: bankData.ifscCode,
                bankName: bankData.bankName,
                branchName: bankData.branchName
            };

            const response = await axios.post(`${baseUrl}/users/verify-bank`, verificationData);

            if (response.data.success) {
                toast.success('All verifications completed! Welcome to the platform!');

                // Store the token and user info
                localStorage.setItem('userInfo', JSON.stringify(response.data.user));
                localStorage.setItem('token', response.data.token);
                localStorage.removeItem('tempUserInfo'); // Clean up temp data

                // Navigate to the appropriate dashboard based on response
                const redirectUrl = response.data.redirectUrl || '/dashboard';

                // Small delay to show success message
                setTimeout(() => {
                    navigate(redirectUrl);
                }, 1500);

            } else {
                toast.error(response.data.message || 'Bank verification failed');
            }

        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Bank verification failed';
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (field, value) => {
        if (field === 'ifscCode') {
            // Convert to uppercase
            const formattedValue = value.toUpperCase().slice(0, 11);
            setBankData(prev => ({ ...prev, [field]: formattedValue }));
        } else if (field === 'accountNumber' || field === 'confirmAccountNumber') {
            // Only allow digits
            const numericValue = value.replace(/\D/g, '');
            setBankData(prev => ({ ...prev, [field]: numericValue }));
        } else {
            setBankData(prev => ({ ...prev, [field]: value }));
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-100 flex items-center justify-center p-4">
            <div className="max-w-md w-full space-y-8">
                {/* Header */}
                <div className="text-center">
                    <h2 className="mt-6 text-3xl font-bold text-gray-900">
                        Bank Verification
                    </h2>
                    <p className="mt-2 text-sm text-gray-600">
                        Final step - Verify your bank account details
                    </p>
                </div>

                {/* Form */}
                <div className="bg-white py-8 px-6 shadow-xl rounded-2xl">
                    <div className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Account Number *
                            </label>
                            <input
                                type="text"
                                value={bankData.accountNumber}
                                onChange={(e) => handleInputChange('accountNumber', e.target.value)}
                                placeholder="Enter account number"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Confirm Account Number *
                            </label>
                            <input
                                type="text"
                                value={bankData.confirmAccountNumber}
                                onChange={(e) => handleInputChange('confirmAccountNumber', e.target.value)}
                                placeholder="Re-enter account number"
                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${bankData.confirmAccountNumber && bankData.accountNumber !== bankData.confirmAccountNumber
                                    ? 'border-red-300 bg-red-50'
                                    : 'border-gray-300'
                                    }`}
                            />
                            {bankData.confirmAccountNumber && bankData.accountNumber !== bankData.confirmAccountNumber && (
                                <p className="mt-1 text-xs text-red-600">Account numbers do not match</p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                IFSC Code *
                            </label>
                            <input
                                type="text"
                                value={bankData.ifscCode}
                                onChange={(e) => handleInputChange('ifscCode', e.target.value)}
                                placeholder="ABCD0123456"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 uppercase"
                            />
                            <p className="mt-1 text-xs text-gray-500">
                                Format: ABCD0123456
                            </p>
                        </div>

                        <div className="grid grid-cols-1 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Bank Name (Optional)
                                </label>
                                <input
                                    type="text"
                                    value={bankData.bankName}
                                    onChange={(e) => handleInputChange('bankName', e.target.value)}
                                    placeholder="Enter bank name"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Branch Name (Optional)
                                </label>
                                <input
                                    type="text"
                                    value={bankData.branchName}
                                    onChange={(e) => handleInputChange('branchName', e.target.value)}
                                    placeholder="Enter branch name"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                />
                            </div>
                        </div>

                        <button
                            onClick={handleVerifyBank}
                            disabled={loading}
                            className="w-full bg-purple-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                        >
                            {loading ? (
                                <div className="flex items-center justify-center">
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Verifying...
                                </div>
                            ) : (
                                'Complete Verification'
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BankVerificationScreen;